import { useEffect, useMemo, useState } from "react";
import { Modal } from "../shared/Modal";
import { Button } from "../shared/Button";
import { Spinner } from "../shared/Spinner";
import { api, type Snippet } from "../../api/client";
import { useToastStore } from "../../stores/useToastStore";
import { formatApiError } from "../../lib/api-errors";
import { slugify } from "../../../shared/doc-paths";
import {
  matchSections,
  splitIntoSections,
  type SectionMatch,
} from "../../lib/snippet-split";

interface SnippetSplitPanelProps {
  markdown: string;
  docType: string;
  onClose: () => void;
}

type RowStatus = "idle" | "saving" | "saved";

export function SnippetSplitPanel({
  markdown,
  docType,
  onClose,
}: SnippetSplitPanelProps) {
  const addToast = useToastStore((s) => s.addToast);
  const [library, setLibrary] = useState<Snippet[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<Record<string, RowStatus>>({});

  // Sections with a real heading are the savable snippet candidates.
  const sections = useMemo(
    () => splitIntoSections(markdown).filter((s) => s.heading.trim()),
    [markdown],
  );

  useEffect(() => {
    let cancelled = false;
    api.snippets
      .list()
      .then((list) => {
        if (!cancelled) setLibrary(list);
      })
      .catch((e) => {
        if (!cancelled) setError(formatApiError(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const matches = useMemo<SectionMatch[]>(
    () => (library ? matchSections(sections, library) : []),
    [sections, library],
  );

  const saveRow = async (m: SectionMatch) => {
    const slug = slugify(m.section.heading);
    if (!slug) return;
    setStatus((s) => ({ ...s, [slug]: "saving" }));
    try {
      await api.snippets.save(slug, {
        title: m.section.heading,
        category: docType || "methodology",
        description: "",
        content: m.section.content,
      });
      setStatus((s) => ({ ...s, [slug]: "saved" }));
      addToast("snippet saved", "success");
    } catch {
      setStatus((s) => ({ ...s, [slug]: "idle" }));
      addToast("save failed", "error");
    }
  };

  const newRows = matches.filter((m) => !m.match);
  const savableLeft = newRows.filter(
    (m) => status[slugify(m.section.heading)] !== "saved",
  );

  const saveAllNew = async () => {
    for (const m of savableLeft) {
      // sequential so the commits don't race in the docs repo
      // eslint-disable-next-line no-await-in-loop
      await saveRow(m);
    }
  };

  const footer = (
    <>
      {savableLeft.length > 0 && (
        <Button variant="primary" onClick={saveAllNew}>
          save {savableLeft.length} new
        </Button>
      )}
      <Button onClick={onClose}>close</Button>
    </>
  );

  return (
    <Modal title="split into snippets" onClose={onClose} footer={footer}>
      {error && <p style={styles.error}>{error}</p>}
      {!library && !error && (
        <div style={styles.loading}>
          <Spinner size={20} label="loading…" />
        </div>
      )}
      {library && matches.length === 0 && (
        <p style={styles.empty}>
          no sections found — add ## headings to split this document
        </p>
      )}
      {library && matches.length > 0 && (
        <div style={styles.list}>
          {matches.map((m) => {
            const slug = slugify(m.section.heading);
            const rowStatus = status[slug] ?? "idle";
            const saved = rowStatus === "saved";
            return (
              <div key={slug} style={styles.row}>
                <div style={styles.rowMain}>
                  <span style={styles.heading}>{m.section.heading}</span>
                  <StatusLine match={m} saved={saved} />
                </div>
                {!m.match && !saved && (
                  <button
                    type="button"
                    style={styles.saveBtn}
                    disabled={rowStatus === "saving"}
                    onClick={() => saveRow(m)}
                  >
                    {rowStatus === "saving" ? "saving…" : "save to library"}
                  </button>
                )}
                {m.match?.kind === "similar" && !saved && (
                  <button
                    type="button"
                    style={styles.saveBtn}
                    disabled={rowStatus === "saving"}
                    onClick={() => saveRow(m)}
                  >
                    {rowStatus === "saving" ? "saving…" : "save as new"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

function StatusLine({ match, saved }: { match: SectionMatch; saved: boolean }) {
  if (saved)
    return (
      <span style={{ ...styles.status, color: "var(--comp-green)" }}>
        saved to library
      </span>
    );
  if (!match.match)
    return <span style={{ ...styles.status, color: "var(--fg-3)" }}>new</span>;
  if (match.match.kind === "exact") {
    return (
      <span style={{ ...styles.status, color: "var(--comp-green)" }}>
        in library
      </span>
    );
  }
  const pct = Math.round(match.match.score * 100);
  return (
    <span style={{ ...styles.status, color: "var(--comp-mustard)" }}>
      similar: {match.match.snippet.title} ({pct}%)
    </span>
  );
}

const styles: Record<string, React.CSSProperties> = {
  error: { color: "var(--comp-salmon)", fontSize: 12, margin: 0 },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: "var(--space-4)",
  },
  empty: { fontSize: 12, color: "var(--fg-3)", margin: 0 },
  list: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "50vh",
    overflowY: "auto",
    margin: "0 calc(-1 * var(--space-4))",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-3)",
    padding: "8px var(--space-4)",
    borderTop: "1px solid var(--comp-rule)",
  },
  rowMain: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
    flex: 1,
  },
  heading: {
    fontSize: 13,
    color: "var(--fg-1)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  status: { fontFamily: "var(--font-mono)", fontSize: 11 },
  saveBtn: {
    flexShrink: 0,
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-2)",
    background: "none",
    border: "none",
    padding: "2px 0",
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
};
